import type { Mode, PartyIndex, SupportLine } from '@divorceos/schema';

/** "1,234" with negatives in parentheses; blanks for non-finite. */
export function fmt$(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '';
  const r = Math.round(n);
  const s = Math.abs(r).toLocaleString('en-US', { maximumFractionDigits: 0 });
  return r < 0 ? `(${s})` : s;
}

/** Signed money: "-1,234" / "1,234" (used where the original prints a minus sign). */
export function fmtSigned(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '';
  const r = Math.round(n);
  const s = Math.abs(r).toLocaleString('en-US', { maximumFractionDigits: 0 });
  return r < 0 ? `-${s}` : s;
}

/** Percent with `digits` decimals and a trailing %. */
export function fmtPct(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '';
  return `${n.toFixed(digits)}%`;
}

/** Time-share percent: up to 2 decimals, trailing zeros trimmed, trailing %. */
export function fmtTimeshare(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '';
  return `${fmtInput(n, 2)}%`;
}

/** Plain number for input display (no grouping; up to 2 decimals, trailing zeros trimmed). */
export function fmtInput(n: number, decimals = 2): string {
  if (!Number.isFinite(n)) return '';
  const s = n.toFixed(decimals);
  return s.replace(/\.?0+$/, '');
}

/** Parses "1,234", "(1,234)", "-1234", "$1,234.50", "" (-> 0). NaN on garbage. */
export function parseNumber(text: string): number {
  const t = text.trim();
  if (t === '') return 0;
  const neg = /^\(.*\)$/.test(t);
  const cleaned = t.replace(/[(),$\s%]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return 0;
  const v = Number(cleaned);
  if (Number.isNaN(v)) return NaN;
  return neg ? -Math.abs(v) : v;
}

/** Monthly stored value -> displayed value for the current mode. */
export function toDisplay(monthly: number, mode: Mode): number {
  return mode === 'ANNUAL' ? monthly * 12 : monthly;
}

/** Displayed value (per mode) -> monthly stored value. */
export function fromDisplay(displayed: number, mode: Mode): number {
  return mode === 'ANNUAL' ? displayed / 12 : displayed;
}

/** Support line "1,234 (Husband)" or "1,234" when no payor. */
export function fmtSupport(line: SupportLine | undefined, labels: [string, string], mode: Mode = 'MONTHLY'): string {
  if (!line) return '';
  const amt = fmt$(toDisplay(line.amount, mode));
  return line.payor === null ? amt : `${amt} ${payorTag(line.payor, labels)}`;
}

export function payorTag(p: PartyIndex | null, labels: [string, string]): string {
  return p === null ? '' : labels[p].slice(0, 1).toUpperCase();
}

export function payorName(p: PartyIndex | null, labels: [string, string]): string {
  return p === null ? '' : labels[p];
}

/** "yyyy-mm-dd" -> "mm/dd/yyyy" for display. */
export function fmtDate(iso: string | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : iso;
}

export function fmtDateTime(s: string | undefined | null): string {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString();
}
